import * as THREE from 'three';

// Loads public/textures/soundboard.<hash>.{webp,jpg} + soundboard.json once,
// at application startup, and hands the resolved texture to whoever asks.
// Built offline by `npm run build:assets` (tools/build-soundboard.mjs) -
// nothing here does any image processing. Same preload/get shape as
// crowd-atlas.js, for the same reason: resonance.js's background must be
// passed in via options at construction, never fetched inside init(), so a
// visualizer swap never shows an empty/black frame while a texture loads.
//
// RELATIVE urls on purpose - see crowd-atlas.js's own comment: express.static
// sends no CORS headers, so an absolute cross-origin fetch would break when
// this page runs from the Tizen app's own file:// origin.
const MANIFEST_URL = 'textures/soundboard.json';

let _texture = null;
let _promise = null;

function configure(texture) {
  texture.colorSpace = THREE.SRGBColorSpace;
  texture.wrapS = THREE.RepeatWrapping;
  texture.wrapT = THREE.RepeatWrapping;
  texture.generateMipmaps = true;
  texture.minFilter = THREE.LinearMipmapLinearFilter;
  texture.magFilter = THREE.LinearFilter;
  texture.anisotropy = 1;
  texture.needsUpdate = true;
  return texture;
}

function loadTexture(url) {
  return new Promise(function (resolve, reject) {
    new THREE.TextureLoader().load(url, resolve, undefined, function () {
      reject(new Error(url + ' failed to load'));
    });
  });
}

// Resolves to a configured THREE.Texture, or to null. Never rejects: a
// missing/failed texture is a degraded visualizer (resonance.js renders on
// black instead), not a broken page.
export function preloadSoundboardTexture() {
  if (_promise) return _promise;
  _promise = fetch(MANIFEST_URL)
    .then(function (r) {
      if (!r.ok) throw new Error('soundboard.json HTTP ' + r.status);
      return r.json();
    })
    .then(function (manifest) {
      // WebP first (the whole point of the build script's byte-budget
      // pass), falling back to the JPEG the build always writes alongside
      // it for browsers/devices without WebP decode support.
      return loadTexture('textures/' + manifest.webp).catch(function () {
        return loadTexture('textures/' + manifest.jpg);
      });
    })
    .then(function (texture) {
      _texture = configure(texture);
      return _texture;
    })
    .catch(function (err) {
      console.error('[party-mode] soundboard texture unavailable, resonance will render on black', err);
      _texture = null;
      return null;
    });
  return _promise;
}

// Synchronous accessor for construction sites that cannot await (the
// switcher's factories, adaptive-party's lazy mode swap). Returns null until
// the preload above has resolved.
export function getSoundboardTexture() {
  return _texture;
}

// The texture is shared and preloaded: whoever uses it must NOT dispose it.
// Exposed only so the leak test can account for the one texture it adds.
export function isSoundboardTextureLoaded() {
  return _texture !== null;
}
